import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Card, Button, Space, Tag, Tooltip, Progress, Statistic, Row, Col, Alert, Avatar, Badge } from 'antd';
import {
	PlayCircleOutlined,
	PauseCircleOutlined,
	StopOutlined,
	WarningOutlined,
	EyeOutlined,
	MessageOutlined,
	UserOutlined,
	ClockCircleOutlined,
	VideoCameraOutlined,
	WifiOutlined,
	FlagOutlined,
	CheckCircleOutlined,
	FullscreenOutlined,
	SoundOutlined,
	SoundFilled,
	BlockOutlined,
	CloseOutlined,
} from '@ant-design/icons';
import flvjs from 'flv.js';
import './StreamPreview.scss';

// module 
import { byteliveGetPlaybackUrl } from '../../../../modules/bytelive';

class StreamPreview extends Component {
	constructor(props) {
		super(props);
		this.state = {
			isPlaying: false,
			volume: 0,
			isMuted: true,
			previousVolume: 30,
			showVolumeSlider: false,
			isFullscreen: false,
			showControls: true,
			connectionQuality: 'excellent',
			chatMessages: [],
			streamHealth: {
				fps: 60,
				bitrate: 3500,
				droppedFrames: 0.2,
				latency: 2.1
			},
			// FLV Player 相關狀態
			playbackUrl: null,
			loadingVideo: false,
			videoError: null,
			playerReady: false
		};
		this.videoRef = React.createRef();
		this.controlsTimeout = null;
		this.flvPlayer = null;
	}

	componentDidMount() {
		this.initializeFlvPlayer();
		this.loadChatMessages();
		this.startHealthMonitoring();
	}

	componentWillUnmount() {
		this.stopHealthMonitoring();
		this.destroyFlvPlayer();
		if (this.controlsTimeout) {
			clearTimeout(this.controlsTimeout);
		}
	}

	componentDidUpdate(prevProps) {
		if (prevProps.stream?.id !== this.props.stream?.id) {
			this.loadChatMessages();
			this.resetPlayer();
			this.loadPlaybackUrl();
		}
	}

	// 初始化 FLV Player
	initializeFlvPlayer = async () => {
		if (!flvjs.isSupported()) {
			console.error('FLV.js not supported!');
			this.setState({ videoError: 'Browser not supported for FLV playback' });
			return;
		}

		const video = this.videoRef.current;
		if (!video) return;

		try {
			// 設置視頻事件監聽
			video.addEventListener('loadedmetadata', this.onVideoLoadedMetadata);
			video.addEventListener('play', this.onVideoPlay);
			video.addEventListener('pause', this.onVideoPause);
			video.addEventListener('volumechange', this.onVideoVolumeChange);
			video.addEventListener('error', this.onVideoError);
			video.addEventListener('canplay', this.onVideoCanPlay);

			// 載入播放 URL
			this.loadPlaybackUrl();
		} catch (error) {
			console.error('Failed to initialize FLV Player:', error);
			this.setState({ videoError: 'Failed to initialize video player' });
		}
	}

	// 銷毀 FLV Player
	destroyFlvPlayer = async () => {
		if (this.flvPlayer) {
			try {
				this.flvPlayer.pause();
				this.flvPlayer.unload();
				this.flvPlayer.detachMediaElement();
				this.flvPlayer.destroy();
				this.flvPlayer = null;
			} catch (error) {
				console.error('Error destroying FLV Player:', error);
			}
		}
	}

	// 載入播放 URL
	loadPlaybackUrl = async () => {
		const { stream, byteliveGetPlaybackUrl } = this.props;
		if (!stream || !byteliveGetPlaybackUrl) return;

		this.setState({ loadingVideo: true, videoError: null });

		try {
			const response = await byteliveGetPlaybackUrl({
				contentId: stream.contentId,
				contentType: 'LiveProgram'
			});

			if (response?.media?.flv?.url) {
				this.setState({ playbackUrl: response.media.flv.url });
				await this.loadVideo(response.media.flv.url);
			} else {
				throw new Error('Invalid playback URL response');
			}
		} catch (error) {
			console.error('Failed to load playback URL:', error);
			this.setState({ 
				videoError: 'Failed to load video',
				loadingVideo: false 
			});
		}
	}

	// 載入視頻
	loadVideo = async (url) => {
		if (!url) return;

		try {
			// 先銷毀之前的播放器
			if (this.flvPlayer) {
				this.destroyFlvPlayer();
			}

			const video = this.videoRef.current;
			if (!video) return;

			// 創建新的 FLV 播放器
			this.flvPlayer = flvjs.createPlayer({
				type: 'flv',
				url: url,
				isLive: true,
				hasAudio: true,
				hasVideo: true,
				cors: true,
				withCredentials: false
			});

			// 設置 FLV 播放器事件監聽
			this.flvPlayer.on(flvjs.Events.ERROR, this.onFlvError);
			this.flvPlayer.on(flvjs.Events.LOADING_COMPLETE, this.onFlvLoadingComplete);
			this.flvPlayer.on(flvjs.Events.RECOVERED_EARLY_EOF, this.onFlvRecovered);

			// 附加到視頻元素
			this.flvPlayer.attachMediaElement(video);
			this.flvPlayer.load();

			this.setState({ 
				loadingVideo: false,
				playerReady: true 
			});

		} catch (error) {
			console.error('Failed to load video:', error);
			this.setState({ 
				videoError: 'Failed to load video stream',
				loadingVideo: false 
			});
		}
	}

	// FLV Player 錯誤處理
	onFlvError = (errorType, errorDetail, errorInfo) => {
		console.error('FLV Player Error:', errorType, errorDetail, errorInfo);
		this.setState({ videoError: 'Video playback error occurred' });
	}

	// FLV 載入完成
	onFlvLoadingComplete = () => {
		console.log('FLV loading complete');
	}

	// FLV 恢復錯誤
	onFlvRecovered = () => {
		console.log('FLV recovered from early EOF');
	}

	// 視頻元數據載入完成
	onVideoLoadedMetadata = () => {
		console.log('Video metadata loaded');
	}

	// 視頻可以播放
	onVideoCanPlay = () => {
		this.setState({ loadingVideo: false });
	}

	// 視頻播放
	onVideoPlay = () => {
		this.setState({ isPlaying: true });
	}

	// 視頻暫停
	onVideoPause = () => {
		this.setState({ isPlaying: false });
	}

	// 視頻錯誤
	onVideoError = (event) => {
		console.error('Video error:', event);
		this.setState({ videoError: 'Video playback error' });
	}

	// 音量變化
	onVideoVolumeChange = () => {
		const video = this.videoRef.current;
		if (video) {
			const currentVolume = Math.round(video.volume * 100);
			const isMuted = video.muted;
			
			this.setState({ 
				volume: isMuted ? 0 : currentVolume,
				isMuted: isMuted,
				// 只有在不是靜音狀態且音量大於 0 時才更新 previousVolume
				previousVolume: (!isMuted && currentVolume > 0) ? currentVolume : this.state.previousVolume
			});
		}
	}

	// 切換靜音
	handleMuteToggle = () => {
		const video = this.videoRef.current;
		if (!video) return;

		const currentVolume = this.state.volume;
		const isMuted = this.state.isMuted;

		if (isMuted) {
			// 如果目前是靜音狀態，恢復到之前的音量
			const restoreVolume = this.state.previousVolume;
			video.volume = restoreVolume / 100;
			video.muted = false;
			
			this.setState({ 
				isMuted: false,
				volume: restoreVolume
			});
		} else {
			// 如果目前不是靜音，保存當前音量並靜音
			video.muted = true;
			video.volume = 0;
			
			this.setState({ 
				isMuted: true,
				previousVolume: currentVolume, // 保存當前音量
				volume: 0
			});
		}
	}

	// 處理音量變化
	handleVolumeChange = (value) => {
		const video = this.videoRef.current;
		if (!video) return;

		video.volume = value / 100;
		
		if (value === 0) {
			// 如果滑桿調到 0，設為靜音狀態
			video.muted = true;
			this.setState({ 
				volume: 0,
				isMuted: true
			});
		} else {
			// 如果滑桿不是 0，取消靜音並更新音量
			video.muted = false;
			this.setState({ 
				volume: value,
				isMuted: false,
				previousVolume: value // 更新 previousVolume
			});
		}
	}

	// 顯示/隱藏音量滑桿
	handleVolumeHover = (show) => {
		this.setState({ showVolumeSlider: show });
	}

	// 畫中畫功能
	handlePictureInPicture = async () => {
		const video = this.videoRef.current;
		if (!video) return;

		try {
			if (document.pictureInPictureElement) {
				await document.exitPictureInPicture();
			} else if (video.requestPictureInPicture) {
				await video.requestPictureInPicture();
			} else {
				console.warn('Picture-in-Picture not supported');
			}
		} catch (error) {
			console.error('Picture-in-Picture failed:', error);
		}
	}

	// Load chat messages for the stream
	loadChatMessages = () => {
		if (!this.props.stream) return;

		// Mock chat messages
		const mockMessages = [
			{ id: 1, user: 'User123', message: 'Great stream!', timestamp: Date.now() - 30000 },
			{ id: 2, user: 'Viewer456', message: 'Amazing content', timestamp: Date.now() - 25000 },
			{ id: 3, user: 'Fan789', message: 'Keep it up!', timestamp: Date.now() - 20000 },
			{ id: 4, user: 'Supporter', message: 'Love this game', timestamp: Date.now() - 15000 },
			{ id: 5, user: 'ChatMod', message: 'Welcome everyone!', timestamp: Date.now() - 10000 }
		];

		this.setState({ chatMessages: mockMessages });
	}

	// Start monitoring stream health
	startHealthMonitoring = () => {
		this.healthInterval = setInterval(() => {
			if (this.props.stream && this.props.stream.status === 'live') {
				this.updateStreamHealth();
			}
		}, 2000);
	}

	// Stop monitoring stream health
	stopHealthMonitoring = () => {
		if (this.healthInterval) {
			clearInterval(this.healthInterval);
		}
	}

	// Update stream health metrics
	updateStreamHealth = () => {
		const health = {
			fps: 58 + Math.random() * 4,
			bitrate: 3400 + Math.random() * 200,
			droppedFrames: Math.random() * 1,
			latency: 1.8 + Math.random() * 0.6
		};

		this.setState({ streamHealth: health });
	}

	// Reset player state
	resetPlayer = () => {
		this.destroyFlvPlayer();
		this.setState({
			isPlaying: false,
			volume: 50,
			previousVolume: 50,
			isFullscreen: false,
			playbackUrl: null,
			videoError: null,
			playerReady: false
		});
	}

	// Handle play/pause
	handlePlayPause = () => {
		const video = this.videoRef.current;
		if (!video || !this.state.playerReady) return;

		if (this.state.isPlaying) {
			video.pause();
		} else {
			video.play().catch(error => {
				console.error('Play failed:', error);
				this.setState({ videoError: 'Failed to start playback' });
			});
		}
	}

	// Handle volume change
	handleVolumeChange = (value) => {
		const video = this.videoRef.current;
		if (!video) return;

		video.volume = value / 100;
		this.setState({ volume: value });
	}

	// Handle fullscreen toggle
	handleFullscreen = () => {
		if (!document.fullscreenElement) {
			this.videoRef.current?.requestFullscreen();
		} else {
			document.exitFullscreen();
		}
		
		this.setState(prevState => ({
			isFullscreen: !prevState.isFullscreen
		}));
	}

	// Show/hide controls
	handleMouseMove = () => {
		this.setState({ showControls: true });
		
		if (this.controlsTimeout) {
			clearTimeout(this.controlsTimeout);
		}
		
		this.controlsTimeout = setTimeout(() => {
			this.setState({ showControls: false });
		}, 3000);
	}

	// Format duration
	calculateLiveDuration = (liveStartTime, liveEndTime) => {
		if (!liveStartTime) {
			return '-';
		}
		
		try {
			const startTime = new Date(liveStartTime);
			const currentTime = liveEndTime ? new Date(liveEndTime) : new Date();
			
			// 檢查 liveStartTime 是否為未來時間
			if (startTime > currentTime) {
				return '-';
			}
			
			// 計算時間差（毫秒）
			const diffMs = currentTime - startTime;
			
			// 轉換為秒
			const totalSeconds = Math.floor(diffMs / 1000);
			
			// 計算小時、分鐘、秒
			const hours = Math.floor(totalSeconds / 3600);
			const minutes = Math.floor((totalSeconds % 3600) / 60);
			const seconds = totalSeconds % 60;
			
			// 格式化為 HH:MM:SS
			return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
			
		} catch (error) {
			console.error('Error calculating live duration:', error);
			return '-';
		}
	}

	getStatusTag = (record) => {
		let status;

		if (record.isLive) {
			status = 'live';
		} else if (!record.liveEndTime) {
			status = 'scheduled';
		} else if (record.liveEndTime) {
			status = 'processing';
		} else {
			status = 'error';
		}

		const statusConfig = {
			live: { color: 'success', text: 'Live' },
			scheduled: { color: 'default', text: 'Scheduled' },
			error: { color: 'error', text: 'Error' },
			processing: { color: 'processing', text: 'Processing' }
		};

		const config = statusConfig[status.toLowerCase()] || statusConfig.offline;
		return <Tag color={config.color}>{config.text}</Tag>;
	}

	// Format number
	formatNumber = (num) => {
		if (num >= 1000000) {
			return `${(num / 1000000).toFixed(1)}M`;
		}
		if (num >= 1000) {
			return `${(num / 1000).toFixed(1)}K`;
		}
		return Math.floor(num).toString();
	}

	// Get connection quality color
	getConnectionColor = (quality) => {
		const colors = {
			excellent: '#22c55e',
			good: '#3b82f6',
			fair: '#f59e0b',
			poor: '#ef4444'
		};
		return colors[quality] || '#9ca3af';
	}

	// Get health status color
	getHealthColor = (value, thresholds) => {
		if (value >= thresholds.excellent) return '#22c55e';
		if (value >= thresholds.good) return '#3b82f6';
		if (value >= thresholds.fair) return '#f59e0b';
		return '#ef4444';
	}

	// Render stream player
	renderStreamPlayer = () => {
		const { stream } = this.props;
		const { isPlaying, volume, showControls, loadingVideo, videoError, playerReady } = this.state;

		if (!stream) {
			return (
				<div className="no-stream-placeholder">
					<VideoCameraOutlined />
					<p>Select a stream to preview</p>
				</div>
			);
		}

		// 顯示錯誤狀態
		if (videoError) {
			return (
				<div className="no-stream-placeholder">
					<WarningOutlined style={{ color: '#ef4444' }} />
					<p>{videoError}</p>
					<Button size="small" onClick={this.loadPlaybackUrl}>
						Retry
					</Button>
				</div>
			);
		}

		return (
			<div 
				className="stream-player"
				onMouseMove={this.handleMouseMove}
				onMouseLeave={() => this.setState({ showControls: false })}
			>
				<div className="video-container">
					<video
						ref={this.videoRef}
						className="video-element"
						poster={stream.thumbnailUrl}
						muted={volume === 0}
						playsInline
						autoPlay={true}
						controls={false}
					>
						Your browser does not support the video tag.
					</video>
					
					{/* 載入中覆蓋層 */}
					{loadingVideo && (
						<div className="loading-overlay">
							<div className="loading-spinner">Loading...</div>
						</div>
					)}
					
					{/* Video overlay */}
					<div className="video-overlay">
						{/* Stream info overlay */}
						<div className="stream-info-overlay">
							<Tag color="red" className="live-indicator">
								<PlayCircleOutlined /> LIVE
							</Tag>
							{/* <div className="viewer-count">
								<EyeOutlined />
								<span>{this.formatNumber(stream.viewerCount)}</span>
							</div> */}
						</div>

						{/* Controls overlay */}
						{showControls && playerReady && (
							<div className="controls-overlay">
								<div className="control-buttons">
									{/* 播放/暫停按鈕 */}
									<Button
										type="text"
										size="large"
										icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
										onClick={this.handlePlayPause}
										className="play-btn"
									/>
									
									{/* 音量控制 */}
									<div 
										className="volume-control"
										onMouseEnter={() => this.handleVolumeHover(true)}
										onMouseLeave={() => this.handleVolumeHover(false)}
									>
										<Button
											type="text"
											icon={
												this.state.isMuted || this.state.volume === 0 ? 
												<SoundOutlined /> : 
												<SoundFilled />
											}
											onClick={this.handleMuteToggle}
											className="volume-btn"
										/>
										
										{/* 音量滑桿 - 移到 volume-control 內部 */}
										{this.state.showVolumeSlider && (
											<div className="volume-slider-container">
												<input
													type="range"
													min="0"
													max="100"
													value={this.state.volume}
													onChange={(e) => this.handleVolumeChange(parseInt(e.target.value))}
													className="volume-slider"
													orient="vertical"
												/>
												<span className="volume-value">{this.state.volume}</span>
											</div>
										)}
									</div>
									
									{/* 畫中畫按鈕 */}
									<Button
										type="text"
										icon={<BlockOutlined />}
										onClick={this.handlePictureInPicture}
										className="pip-btn"
									/>
									
									{/* 全螢幕按鈕 */}
									<Button
										type="text"
										icon={<FullscreenOutlined />}
										onClick={this.handleFullscreen}
										className="fullscreen-btn"
									/>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		);
	}

	// Render stream info
	renderStreamInfo = () => {
		const { stream } = this.props;
		
		if (!stream) return null;
		console.log('stream', stream);
		return (
			<div className="stream-info-section">
				<div className="stream-header">
					<div className="stream-title">
						<h4>{stream.title}</h4>
						{stream.isVerified && (
							<CheckCircleOutlined style={{ color: '#22c55e', marginLeft: 8 }} />
						)}
					</div>
					<div className="stream-meta">
						<Space direction="vertical" size="small">
							<div className="streamer-info">
								<Avatar size="small" icon={<UserOutlined />} />
								<span className="streamer-name">{stream.cast.join(", ")}</span>
								{/* <Tag size="small">{stream.category}</Tag> */}
							</div>
							<div className="stream-stats">
								<Space size="middle">
									<span>
										<ClockCircleOutlined style={{ marginRight: 4 }} />
										{this.calculateLiveDuration(stream.liveStartTime)}
									</span>
									{/* <span>
										<EyeOutlined style={{ marginRight: 4 }} />
										{this.formatNumber(stream.viewerCount)}
									</span> */}
									{this.getStatusTag(stream)}
								</Space>
							</div>
						</Space>
					</div>
				</div>

				{/* Alerts */}
				{stream.isReported && (
					<Alert
						type="warning"
						message="This stream has pending reports"
						icon={<FlagOutlined />}
						showIcon
						style={{ marginTop: 12 }}
					/>
				)}
			</div>
		);
	}

	// Render technical metrics
	renderTechnicalMetrics = () => {
		const { stream } = this.props;
		const { streamHealth } = this.state;
		
		if (!stream || stream.status !== 'live') return null;

		return (
			<div className="technical-metrics">
				<h5>Technical Metrics</h5>
				<Row gutter={[12, 12]}>
					<Col span={12}>
						<Statistic
							title="FPS"
							value={streamHealth.fps}
							precision={0}
							valueStyle={{ 
								color: this.getHealthColor(streamHealth.fps, {
									excellent: 58, good: 50, fair: 40
								}),
								fontSize: '16px'
							}}
						/>
					</Col>
					<Col span={12}>
						<Statistic
							title="Bitrate"
							value={streamHealth.bitrate}
							precision={0}
							suffix="kbps"
							valueStyle={{ 
								color: this.getHealthColor(streamHealth.bitrate, {
									excellent: 3000, good: 2000, fair: 1000
								}),
								fontSize: '16px'
							}}
						/>
					</Col>
					<Col span={12}>
						<div className="metric-item">
							<span className="metric-label">Dropped Frames</span>
							<span 
								className="metric-value"
								style={{ 
									color: streamHealth.droppedFrames < 1 ? '#22c55e' : '#ef4444'
								}}
							>
								{streamHealth.droppedFrames.toFixed(1)}%
							</span>
						</div>
					</Col>
					<Col span={12}>
						<div className="metric-item">
							<span className="metric-label">Latency</span>
							<span 
								className="metric-value"
								style={{ 
									color: streamHealth.latency < 3 ? '#22c55e' : '#f59e0b'
								}}
							>
								{streamHealth.latency.toFixed(1)}s
							</span>
						</div>
					</Col>
				</Row>

				{/* Connection Quality */}
				<div className="connection-quality">
					<div className="quality-header">
						<WifiOutlined style={{ marginRight: 6 }} />
						<span>Connection Quality</span>
					</div>
					<Progress
						percent={85}
						strokeColor={this.getConnectionColor('excellent')}
						showInfo={false}
						size="small"
					/>
					<span className="quality-label">Excellent</span>
				</div>
			</div>
		);
	}

	// Render chat preview
	renderChatPreview = () => {
		const { chatMessages } = this.state;
		
		return (
			<div className="chat-preview">
				<div className="chat-header">
					<MessageOutlined style={{ marginRight: 6 }} />
					<span>Live Chat</span>
					<Badge count={chatMessages.length} style={{ marginLeft: 8 }} />
				</div>
				<div className="chat-messages">
					{chatMessages.slice(-5).map(msg => (
						<div key={msg.id} className="chat-message">
							<span className="chat-user">{msg.user}:</span>
							<span className="chat-text">{msg.message}</span>
						</div>
					))}
				</div>
			</div>
		);
	}

	// Render action buttons
	renderActionButtons = () => {
		const { stream, onEmergencyStop, onStreamWarning } = this.props;
		
		if (!stream) return null;

		return (
			<div className="action-buttons">
				<Space direction="vertical" size="middle" style={{ width: '100%' }}>
					<Button
						type="primary"
						danger
						icon={<StopOutlined />}
						onClick={() => onEmergencyStop(stream.id)}
						block
					>
						Emergency Stop
					</Button>
					<Button
						icon={<WarningOutlined />}
						onClick={() => onStreamWarning(stream.id)}
						block
						style={{ 
							background: '#f59e0b', 
							borderColor: '#f59e0b',
							color: 'white'
						}}
					>
						Send Warning
					</Button>
				</Space>
			</div>
		);
	}

	render() {
		const { stream } = this.props;

		return (
			<Card 
				className="stream-preview"
				title="Stream Preview"
				size="small"
				extra={
					<Tooltip title="Close Preview">
						<Button 
							type="text" 
							icon={<CloseOutlined />} 
							onClick={this.props.onClose}
							size="small"
							style={{ 
								color: '#8c8c8c',
								border: 'none',
								boxShadow: 'none'
							}}
						/>
					</Tooltip>
				}
			>
				<Space direction="vertical" size="middle" style={{ width: '100%' }}>
					{/* Stream Player */}
					{this.renderStreamPlayer()}

					{/* Stream Info */}
					{this.renderStreamInfo()}

					{/* Technical Metrics */}
					{/* {this.renderTechnicalMetrics()} */}

					{/* Chat Preview */}
					{/* {this.renderChatPreview()} */}

					{/* Action Buttons */}
					{/* {this.renderActionButtons()} */}
				</Space>
			</Card>
		);
	}
}

const mapStateToProps = state => ({
	router: state.router,
	userProfile: state.user.userProfile,
	selectedOrganization: state.user.selectedOrganization
});

const mapDispatchToProps = dispatch =>
	bindActionCreators(
		{
			byteliveGetPlaybackUrl,
		},
		dispatch
	);

export default connect(mapStateToProps, mapDispatchToProps)(StreamPreview);
